import type { GridColDef } from '@mui/x-data-grid-premium';
import { cloneDeep } from 'lodash';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type {
    ContactInput,
    CreateProductionFromTemplateInput,
    FieldDefinition,
    LocationInput,
    Production,
    ProductionInput,
    Reference,
    ReferenceInput,
    Schedule
} from 'sr-types/lib/production/v1/graphql';
import {
    Attribute,
    AvailableTypeEnum,
    CUSTOM_FIELD_IMPORT_MAP
} from '../../attributes/attributes';
import type { UserProfile } from '../../common/auth/api';
import { GROUP_BY_FIELDS } from '../../common/components/forms/groupByConfigurations/groupByConstants';
import type { Filter } from '../../common/components/types';
import { PUNCTUATION_MARKS_CONSTANTS, SORT_DIRECTIONS } from '../../common/constants/constants';
import type { ValidatorType } from '../../common/form/Validator';
import { EMPTY_SCHEDULE } from '../../common/form/widgets/scheduleConstants';
import { buildSortTokens, extractReportConfigurations } from '../../common/generateReport/reportUtils';
import type { ReferenceField } from '../../common/reference/reference';
import { getFullName } from '../../person/person';
import { valueGetter } from '../../supply/opportunity/details/ContactListUtils';
import type { ProductionDataListConfigurationState } from '../DataList/ProductionDataListConfiguration/types';
import { getDefaultOwner } from '../form/production/AddProjectOwnerModal';
import type { MarketPlaceBaseItem } from './production';
import { MATERIAL_COLUMN } from './productionImportUtils';

export const EMPTY_PRODUCTION_DATES: { productionDates: Schedule } = {
    productionDates: {
        dateRange: {
            ...cloneDeep(EMPTY_SCHEDULE),
            dateTimeDuration: {
                unit: 'week',
                value: 1
            }
        },
        autoSetDate: false
    }
};

export type ValidationRulesType = {
    [key: string]: ValidatorType[];
};
export type ProductionWithReference = Omit<Production, 'name'> & {
    name: Reference & { managedProduction: string };
    ownerOrganization?: Reference;
};

export type CreateProductionFromTemplateInputWithOwnerOrganization = CreateProductionFromTemplateInput & {
    ownerOrganization?: Reference;
};

export type ProductionInputWithOwnerOrganization = ProductionInput & {
    ownerOrganization?: Reference;
};

export const SORT_BY_PARENT_ORG = 'parentOrganization_ASC';

export const FILTER_FIELD_NAMES = {
    parentOrganization: 'parentOrganization',
    identity: 'identity',
    state: 'state'
};

export const Production_Field_Names = {
    name: 'name',
    publicTitle: 'publicTitle',
    otherTitles: 'otherTitles',
    productionType: 'productionType',
    productionSubType: 'productionSubType',
    genre: 'genre',
    productionStatus: 'productionStatus',
    locations: 'locations',
    scheduleSection: 'scheduleSection',
    productionTrack: 'productionTrack',
    projectStatus: 'projectStatus',
    sectionTitle: 'sectionTitle',
    'productionGroup.productionReference': 'productionGroup.productionReference',
    'summary.about': 'summary.about',
    showAttributes: 'showAttributes',
    'budget.currencyCode': 'budget.currencyCode',
    budget: 'budget',
    ownerOrganization: 'ownerOrganization',
    tags: 'tags'
};

export const Production_Type = {
    developmentProposal: 'Development Proposal'
};

export const updateProductionTemplateValues = (
    production: Production,
    isStepperTemplate: boolean,
    ownerFromUserProfile,
    l10n
) => {
    const { productionType, productionSubType, productionStatus, projectOwnerContacts, budget } = production;
    const owner = projectOwnerContacts || ownerFromUserProfile;
    let updatedValues = {};
    const commonValuesToBeUpdated = {
        ...cloneDeep(EMPTY_PRODUCTION_DATES),
        productionType,
        productionSubType,
        productionStatus,
        managedProduction: true,
        budget: {
            currencyCode: budget?.currencyCode || l10n.defaultCurrency,
            nanos: budget?.nanos,
            units: budget?.units
        }
    };
    if (isStepperTemplate) {
        updatedValues = {
            ...commonValuesToBeUpdated,
            projectOwnerContacts: [],
            projectStatus: production.projectStatus,
            summary: { about: '' },
            attributes: production.attributes,
            associatedFieldDefinitionReferences: production.associatedFieldDefinitionReferences
        };
    } else {
        updatedValues = {
            ...commonValuesToBeUpdated,
            genre: production.genre,
            locations: cloneDeep(production.locations) as LocationInput[],
            // productionDates: p.productionDates, uncomment this if we have form on the ui for creating production template
            productionGroup: production.productionGroup,
            summary: { about: production.about },
            showAttributes: production.showAttributes,
            projectOwnerContacts: owner
        };
    }
    return updatedValues;
};

export type PersonModalStateType = {
    open: boolean;
    prepopulate: {
        name: {
            firstName: string;
            lastName: string;
        };
        email?: string;
    };
};

export type ProductionFormModalType = {
    personModalState: PersonModalStateType;
    setIsSaveDisabled: Dispatch<SetStateAction<boolean>>;
    guestEditorRef: MutableRefObject<any>;
    enableConvertingToManaged: boolean;
    onCreateOwner: (e: any) => void;
    isDialogOpen: boolean;
    setIsDialogOpen: Dispatch<SetStateAction<boolean>>;
    setProductionId: Dispatch<SetStateAction<string>>;
    tempProd: ReferenceInput;
    hiddenFields?: Record<string, boolean>;
    productionFilter?: Filter;
};

type CreateProductionItem = {
    id: string;
    name: string;
    productionAttributes: string;
    managedProduction: boolean;
    reference: ReferenceField;
};

export const getOptionForProductionForm = (item: CreateProductionItem) => {
    return {
        id: item.id,
        label: item.name,
        productionAttributes: item.productionAttributes,
        managedProduction: item.managedProduction,
        ref: { ...item.reference, label: item.name }
    };
};
export const EMPTY_PERSON_MODAL_STATE = {
    open: false,
    prepopulate: { name: { firstName: '', lastName: '' }, email: '' }
};

// Used a generic function to remove ownerOrganization and set ownerOrganizationId as we are using this in multiple places
export const removeOwnerOrganizationAndSetId = <T extends { ownerOrganization?: Reference }>(
    prodData: T
): Omit<T, 'ownerOrganization'> & { ownerOrganizationId: string } => {
    const { ownerOrganization, ...rest } = prodData;
    return {
        ...rest,
        ownerOrganizationId: ownerOrganization?.id || ''
    };
};

export const getFieldType = (fieldType: string) => {
    switch (fieldType) {
        case AvailableTypeEnum.text:
            return 'string';
        case AvailableTypeEnum.number:
            return 'number';
        case AvailableTypeEnum.boolean:
            return 'boolean';
        case AvailableTypeEnum.date:
            return 'string';
        case AvailableTypeEnum.note:
            return 'string';
        default:
            return 'string';
    }
};

export const getCustomFieldNameForImport = (fieldName: string) => {
    return CUSTOM_FIELD_IMPORT_MAP[fieldName as keyof typeof CUSTOM_FIELD_IMPORT_MAP] ?? fieldName;
};

export const convertFieldDefinitionsToColDef = (productionFieldDefinitions: FieldDefinition[] | []) => {
    const colDef: GridColDef[] = [];
    const colDefMeta: Attribute[] = [];

    productionFieldDefinitions.forEach((field: FieldDefinition) => {
        const isMaterialColumn = field.name === MATERIAL_COLUMN;
        const type = getFieldType(field.fieldType);

        const colDefItem: GridColDef = {
            field: getCustomFieldNameForImport(field.fieldName),
            headerName: field.label,
            editable: true,
            sortable: true,
            ...(!isMaterialColumn && {
                valueGetter,
                type,
                flex: 1,
                minWidth: 100,
            }),
            ...(isMaterialColumn && {
                flex: 2,
                minWidth: 300,
            })
        };

        colDef.push(colDefItem);

        colDefMeta.push({
            name: field.fieldName,
            label: field.label,
            fieldType: field.fieldType,
            fieldDefinitionReference: field.reference
        });
    });

    return { colDef, colDefMeta };
};

export const createUpdatedProductionItem = <T extends MarketPlaceBaseItem>(item: T) => {
    return {
        ...item,
        name:
            typeof item.name === 'object' && item.name?.firstName && item.name?.lastName
                ? getFullName(item.name)
                : item.name || item.publicName,
        headline: item.headline || item.summary?.headline || '',
        about: item.about || item.summary?.about || '',
        organization: item.experiences?.[0]?.organizationReference?.label || item.organization || '',
        location: item.location?.label || item.location || item.locations?.[0]?.label || ''
    };
};

/**
 * Derives a default contact input object from the given user profile.
 *
 * This function retrieves the first "default owner" from the provided `userProfile`
 * using {@link getDefaultOwner}, then normalizes its structure into a `ContactInput`
 * shape suitable for use in forms or API calls.
 *
 * It ensures that:
 * - The `name` field always contains string values for `firstName` and `lastName`
 *   (falling back to empty strings if missing).
 * - The `personReference.label` field is populated using {@link getFullName}.
 *
 * @param userProfile - The current user’s profile object, used to look up their default owner.
 * @returns A normalized {@link ContactInput} object representing the default owner contact.
 */
export const getDefaultOwnerContact = (userProfile: UserProfile): ContactInput | undefined => {
    const defaultOwnerProfile = getDefaultOwner(userProfile)?.[0];
    const defaultOwnerPerson = defaultOwnerProfile && {
        ...defaultOwnerProfile,
        name: {
            firstName: defaultOwnerProfile.name?.firstName || '',
            lastName: defaultOwnerProfile.name?.lastName || ''
        },
        personReference: {
            ...defaultOwnerProfile.personReference,
            label: getFullName(defaultOwnerProfile.name)
        }
    };
    return defaultOwnerPerson;
};

/**
 * Generic function to update optimistic state for quick filters in list views.
 *
 * @param facetIdentifier - The identifier of the facet to update (e.g., 'tags').
 * @param results - The results object containing facets.
 * @param updatedItem - The updated item containing the facet values.
 * @param setOptimisticState - Setter function for the optimistic state array.
 */
export type FacetEntry = {
    label: string;
    __typename: string;
    id: string;
};
export function updateOptimisticQuickFilterState<T extends Record<string, any>>(
    facetIdentifier: string,
    results: { facets: Array<{ identifier: string; entries: Array<{ label: string }> }> },
    updatedItem: T,
    setOptimisticState: React.Dispatch<React.SetStateAction<FacetEntry[]>>
) {
    if (!updatedItem) {
        setOptimisticState([]);
        return;
    }
    const findFacet = results.facets?.find((facet) => facet.identifier === facetIdentifier);
    if (findFacet) {
        const values = updatedItem?.[facetIdentifier] || [];
        const missingValues = values.filter(
            (val: string) => !findFacet.entries.some((entry: { label: string }) => entry.label === val)
        );
        if (missingValues.length > 0) {
            const newEntries = missingValues.map((val: string) => {
                const newEntry: FacetEntry = {
                    label: val,
                    __typename: 'SKFacetSetEntry',
                    id: `${facetIdentifier}-${val}`
                };
                return newEntry;
            });
            setOptimisticState((prev) => [...prev, ...newEntries]);
        } else {
            setOptimisticState([]);
        }
    }
}
type ProductionWithRefAndId = ProductionWithReference & { id?: string };
export const onTagChanged = (
    items: Array<ProductionWithRefAndId>,
    updatedItem: ProductionWithRefAndId,
    onChange: (newItems: Array<ProductionWithRefAndId>, updatedItem: ProductionWithRefAndId) => void
) => {
    const newItems = items.map((item) => (item.id === updatedItem.id ? { ...item, ...updatedItem } : item));
    onChange(newItems, updatedItem);
};

/**
 * Generate the GraphQL `sortBy` string for custom workspace sorting.
 *
 * @param workspacePreferenceValue - Partial workspace configuration used to derive sort tokens
 * @returns A `sortBy` string suitable for the API (comma separated tokens) or `undefined` when no custom sort is defined
 */
export const generateCustomSortBy = (
    workspacePreferenceValue: Partial<ProductionDataListConfigurationState> | undefined
): string | undefined => {
    const reportConfiguration = extractReportConfigurations(workspacePreferenceValue);
    const workspaceSortTokens: string[] = buildSortTokens(
        workspacePreferenceValue?.workspaceGroupBy,
        reportConfiguration
    );

    let sortBy: string | undefined = undefined;
    if (workspaceSortTokens.length) {
        const sortCohortsBy = reportConfiguration?.sortCohortsBy;

        const cohortSortTokenMap: Record<string, string> = {
            [GROUP_BY_FIELDS.publicTitleAsc]: `name${SORT_DIRECTIONS.asc}`,
            [GROUP_BY_FIELDS.publicTitleDesc]: `name${SORT_DIRECTIONS.desc}`,
            [GROUP_BY_FIELDS.rankAsc]: `rank${SORT_DIRECTIONS.asc}`,
            [GROUP_BY_FIELDS.rankDesc]: `rank${SORT_DIRECTIONS.desc}`
        };

        const cohortToken = sortCohortsBy ? cohortSortTokenMap[sortCohortsBy] : undefined;
        if (cohortToken) {
            workspaceSortTokens.push(cohortToken);
        }

        sortBy = workspaceSortTokens.join(PUNCTUATION_MARKS_CONSTANTS.comma);
    }

    return sortBy;
};

export const shouldHideShowAttribute = (
    typeValue: string,
    state?: { productionType?: string; productionSubType?: string },
    hiddenFields?: Record<string, boolean>
) => {
    return (
        hiddenFields?.[Production_Field_Names.showAttributes] ||
        (state?.productionType !== typeValue && state?.productionSubType !== typeValue)
    );
};
